import { useEffect, useRef, useState } from "react";
import { api, authenticatedApi } from "../lib/api";
import { useAppState } from "../context/AppState";
import { useLocale } from "../context/Locale";

let addressMapPromise;

function loadGoogleMaps(key) {
  if (window.google?.maps) return Promise.resolve(window.google);
  if (!key) return Promise.reject(new Error("MAP_NOT_CONFIGURED"));
  if (!addressMapPromise) {
    addressMapPromise = new Promise((resolve, reject) => {
      const callback = `deliveryHuarazAddressMap${Date.now()}`;
      window[callback] = () => {
        delete window[callback];
        resolve(window.google);
      };
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&callback=${callback}&loading=async`;
      script.async = true;
      script.onerror = () => reject(new Error("MAP_LOAD_FAILED"));
      document.head.appendChild(script);
    });
  }
  return addressMapPromise;
}

function savedAddressPoint(saved) {
  return {
    lat: Number(saved.latitude),
    lng: Number(saved.longitude),
    address: saved.address,
    house: saved.house || "",
    tag: saved.tag || "Dirección",
    savedId: saved.id,
    accuracy: 0,
  };
}

export default function AddressSheet() {
  const {
    address,
    addressOpen,
    closeAddress,
    saveUser,
    selectAddress,
    user,
  } = useAppState();
  const { t } = useLocale();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [places, setPlaces] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [mapsKey, setMapsKey] = useState("");
  const [draft, setDraft] = useState(null);
  const [query, setQuery] = useState("");
  const [house, setHouse] = useState("");
  const [tag, setTag] = useState("Casa");
  const [saveToProfile, setSaveToProfile] = useState(true);
  const [busy, setBusy] = useState(false);
  const [gpsLocating, setGpsLocating] = useState(false);
  const [gpsError, setGpsError] = useState("");
  const [mapError, setMapError] = useState("");

  async function reverseGeocode(point) {
    try {
      const result = await api("/coordinate-to-address", {
        method: "POST",
        body: JSON.stringify({ lat: point.lat, lng: point.lng }),
      });
      const label = (typeof result === "string" ? result : result?.address) || `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`;
      setDraft((current) => current ? { ...current, address: label } : current);
      setQuery(label);
    } catch {
      const label = `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`;
      setDraft((current) => current ? { ...current, address: label } : current);
      setQuery(label);
    }
  }

  function changeDraft(point, shouldReverse = false) {
    const normalized = {
      ...point,
      lat: Number(point.lat),
      lng: Number(point.lng),
      accuracy: Math.max(0, Number(point.accuracy || 0)),
    };
    setDraft(normalized);
    if (point.address) setQuery(point.address);
    if (shouldReverse) reverseGeocode(normalized);
  }

  useEffect(() => {
    if (!addressOpen) return;
    const current = address || { lat: -9.52779, lng: -77.52778, address: "Huaraz, Áncash", accuracy: 0 };
    setDraft(current);
    setQuery(current.address || "");
    setMapError("");
    api("/popular-geo-locations", { method: "POST", body: "{}" })
      .then((data) => setPlaces(Array.isArray(data) ? data : []))
      .catch(() => setPlaces([]));
    api("/service-hub")
      .then((data) => setMapsKey(data?.maps_key || ""))
      .catch(() => setMapsKey(""));
    if (user?.auth_token) {
      authenticatedApi("/get-addresses", user.auth_token, { method: "POST", body: "{}" })
        .then((rows) => setSavedAddresses(Array.isArray(rows) ? rows : []))
        .catch(() => setSavedAddresses([]));
    } else {
      setSavedAddresses([]);
    }
  }, [addressOpen, user?.auth_token]);

  useEffect(() => {
    if (!addressOpen || !mapsKey || !mapContainer.current || map.current) return;
    let mounted = true;
    loadGoogleMaps(mapsKey)
      .then(({ maps }) => {
        if (!mounted || !mapContainer.current) return;
        const center = { lat: Number(draft?.lat || -9.52779), lng: Number(draft?.lng || -77.52778) };
        map.current = new maps.Map(mapContainer.current, {
          center,
          zoom: 17,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        });
        marker.current = new maps.Marker({ map: map.current, position: center, draggable: true });
        const move = (latLng) => changeDraft({ lat: latLng.lat(), lng: latLng.lng(), address: "", accuracy: 0 }, true);
        marker.current.addListener("dragend", (event) => move(event.latLng));
        map.current.addListener("click", (event) => {
          marker.current.setPosition(event.latLng);
          move(event.latLng);
        });
      })
      .catch((error) => mounted && setMapError(
        error.message === "MAP_NOT_CONFIGURED"
          ? t("mapNotConfigured")
          : error.message === "MAP_LOAD_FAILED"
            ? t("mapLoadFailed")
            : error.message,
      ));
    return () => { mounted = false; };
  }, [addressOpen, mapsKey]);

  useEffect(() => {
    if (!addressOpen || !draft?.lat || !draft?.lng || !map.current || !marker.current) return;
    const position = { lat: Number(draft.lat), lng: Number(draft.lng) };
    marker.current.setPosition(position);
    map.current.panTo(position);
  }, [addressOpen, draft?.lat, draft?.lng]);

  useEffect(() => {
    if (addressOpen) return;
    map.current = null;
    marker.current = null;
  }, [addressOpen]);

  useEffect(() => {
    if (!addressOpen || !address || draft) return;
    changeDraft(address);
  }, [addressOpen, address?.lat, address?.lng]);

  function useGps() {
    if (!navigator.geolocation) {
      setGpsError(t("gpsUnsupported"));
      return;
    }
    setGpsLocating(true);
    setGpsError("");
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      const point = {
        lat: Number(coords.latitude),
        lng: Number(coords.longitude),
        address: "",
        accuracy: Number(coords.accuracy || 0),
      };
      changeDraft(point, true);
      setGpsLocating(false);
    }, (error) => {
      setGpsLocating(false);
      setGpsError(error.code === 1
        ? t("gpsPermissionRequired")
        : t("gpsUnavailableHelp"));
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  }

  async function locateTypedAddress(event) {
    event.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    setMapError("");
    try {
      const google = await loadGoogleMaps(mapsKey);
      new google.maps.Geocoder().geocode({ address: `${query}, Huaraz, Áncash, Perú` }, (results, status) => {
        setBusy(false);
        if (status !== "OK" || !results?.[0]) {
          setMapError(t("addressNotFoundHelp"));
          return;
        }
        const location = results[0].geometry.location;
        changeDraft({ lat: location.lat(), lng: location.lng(), address: results[0].formatted_address, accuracy: 0 });
      });
    } catch (error) {
      setBusy(false);
      setMapError(error.message === "MAP_NOT_CONFIGURED" ? t("mapNotConfigured") : error.message === "MAP_LOAD_FAILED" ? t("mapLoadFailed") : error.message);
    }
  }

  async function confirmAddress() {
    if (!draft?.lat || !draft?.lng) return;
    setBusy(true);
    setMapError("");
    try {
      let selected = { ...draft, house: house.trim(), tag: tag.trim() || "Casa" };
      if (user?.auth_token && saveToProfile && !draft.savedId) {
        const rows = await authenticatedApi("/save-address", user.auth_token, {
          method: "POST",
          body: JSON.stringify({
            latitude: draft.lat,
            longitude: draft.lng,
            address: draft.address || query,
            house: house.trim(),
            tag: tag.trim() || "Casa",
          }),
        });
        const saved = Array.isArray(rows) ? rows[0] : null;
        if (saved) {
          selected = savedAddressPoint(saved);
          saveUser({ ...user, default_address_id: saved.id, default_address: saved });
        }
      }
      selectAddress(selected);
    } catch (error) {
      setMapError(error.message);
    } finally {
      setBusy(false);
    }
  }

  function useSavedAddress(saved) {
    const selected = savedAddressPoint(saved);
    saveUser({ ...user, default_address_id: saved.id, default_address: saved });
    authenticatedApi("/set-default-address", user.auth_token, {
      method: "POST",
      body: JSON.stringify({ address_id: saved.id }),
    }).catch(() => {});
    selectAddress(selected);
  }

  if (!addressOpen) return null;

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={closeAddress}>
      <section className="address-sheet address-sheet--map" role="dialog" aria-modal="true" aria-labelledby="address-sheet-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="address-sheet__handle" />
        <button className="address-sheet__close" type="button" onClick={closeAddress}>×</button>
        <h2 id="address-sheet-title">{t("confirmAddressTitle")}</h2>
        <p>{t("addressMapInstructions")}</p>

        {savedAddresses.length > 0 && (
          <div className="address-sheet__saved">
            <small>{t("savedAddressesTitle")}</small>
            <div>{savedAddresses.map((saved) => (
              <button type="button" key={saved.id} onClick={() => useSavedAddress(saved)}>
                <span>{saved.tag === "Trabajo" ? "🏢" : saved.tag === "Hotel" ? "🏨" : "🏠"}</span>
                <span><b>{saved.tag || t("genericAddress")}</b><small>{saved.house ? `${saved.house}, ` : ""}{saved.address}</small></span>
              </button>
            ))}</div>
          </div>
        )}

        <button className="address-sheet__gps" type="button" onClick={useGps} disabled={gpsLocating}>
          <span>⌖</span><b>{gpsLocating ? t("locating") : t("useGpsNow")}</b>
        </button>
        {Number(draft?.accuracy) > 0 && <div className={`address-sheet__accuracy ${Number(draft.accuracy) > 100 ? "is-low" : ""}`}>
          {t("gpsAccuracy")}: {Math.round(Number(draft.accuracy))} m. {Number(draft.accuracy) > 100 ? t("gpsMoveMarker") : t("gpsConfirmPoint")}
        </div>}

        <form className="address-sheet__search" onSubmit={locateTypedAddress}>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("addressSearchPlaceholder")} />
          <button type="submit" disabled={busy}>{t("search")}</button>
        </form>

        <div className="address-sheet__map-wrap">
          {mapError && !mapsKey ? <div className="address-sheet__map-fallback">{mapError}</div> : <div className="address-sheet__map" ref={mapContainer} />}
          <div className="address-sheet__map-help">{t("dragMarkerHelp")}</div>
        </div>

        {(gpsError || mapError) && <div className="address-sheet__error">{gpsError || mapError}</div>}

        {user?.auth_token && !draft?.savedId && (
          <div className="address-sheet__save-fields">
            <label><span>{t("addressHouseLabel")}</span><input value={house} onChange={(event) => setHouse(event.target.value)} placeholder={t("addressHousePlaceholder")} /></label>
            <label><span>{t("saveAs")}</span><select value={tag} onChange={(event) => setTag(event.target.value)}><option value="Casa">{t("homeTag")}</option><option value="Trabajo">{t("workTag")}</option><option value="Hotel">{t("hotelTag")}</option><option value="Otro">{t("otherTag")}</option></select></label>
            <label className="address-sheet__save-check"><input type="checkbox" checked={saveToProfile} onChange={(event) => setSaveToProfile(event.target.checked)} /> {t("saveInMyAddresses")}</label>
          </div>
        )}

        <button className="address-sheet__confirm" type="button" disabled={busy || !draft?.lat || !draft?.lng} onClick={confirmAddress}>
          {busy ? t("savingAddress") : t("confirmThisAddress")}
        </button>

        {places.length > 0 && (
          <details className="address-sheet__popular"><summary>{t("popularHuarazPlaces")}</summary><div>{places.map((place) => (
            <button type="button" key={place.id} onClick={() => changeDraft({ lat: place.latitude, lng: place.longitude, address: place.address || place.name, accuracy: 0 })}>
              <span>⌖</span><span><b>{place.name}</b>{place.address && <small>{place.address}</small>}</span>
            </button>
          ))}</div></details>
        )}
      </section>
    </div>
  );
}
